import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../../lib/utils';
import { 
  Save, RefreshCw, CheckCircle2, AlertCircle, 
  ShieldCheck, Eye, Search, Copy, Calendar, Laptop, Info
} from 'lucide-react';

const AdminCookies = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Settings State
  const [enabled, setEnabled] = useState(false);
  const [policyUrl, setPolicyUrl] = useState('');
  const [analyticsDesc, setAnalyticsDesc] = useState('');
  const [marketingDesc, setMarketingDesc] = useState('');

  // Logs and Search/Pagination States
  const [logs, setLogs] = useState([]);
  const [searchVal, setSearchVal] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [stats, setStats] = useState({ total: 0, accepted: 0, rejected: 0, custom: 0 });

  useEffect(() => {
    fetchSettings();
  }, []);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchQuery(searchVal);
      setCurrentPage(1);
    }, 450);
    return () => clearTimeout(handler);
  }, [searchVal]);

  // Fetch logs on status/search/page changes
  useEffect(() => {
    fetchLogs();
  }, [currentPage, filterStatus, searchQuery]);

  const fetchSettings = async () => {
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/settings`);
      if (res.data.cookie_settings) {
        const cs = res.data.cookie_settings;
        setEnabled(!!cs.enabled);
        setPolicyUrl(cs.cookie_policy_url || '');
        setAnalyticsDesc(cs.analytics_cookies_desc || '');
        setMarketingDesc(cs.marketing_cookies_desc || '');
      } else {
        // Defaults
        setAnalyticsDesc("Web sitemizin performansını ölçmek, ziyaretçi sayılarını ve trafik kaynaklarını analiz etmek için kullanılır.");
        setMarketingDesc("Kişiselleştirilmiş reklamlar sunmak ve pazarlama kampanyalarımızın etkinliğini ölçmek için kullanılır.");
      }
    } catch (e) {
      console.error("Fetch settings failed", e);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const backendUrl = getBackendUrl();
      const res = await axios.get(`${backendUrl}/api/cookie/logs`, {
        params: {
          page: currentPage,
          limit: 10,
          search: searchQuery,
          status: filterStatus
        },
        withCredentials: true
      });
      
      setLogs(res.data.logs || []);
      setTotalItems(res.data.total_items || 0);
      setTotalPages(res.data.total_pages || 1);
      if (res.data.stats) {
        setStats(res.data.stats);
      }
    } catch (e) {
      console.error("Fetch logs failed", e);
      setError("Çerez onay logları yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess('');
    setError('');

    const cookieSettingsData = {
      enabled,
      cookie_policy_url: policyUrl.trim(),
      analytics_cookies_desc: analyticsDesc.trim(),
      marketing_cookies_desc: marketingDesc.trim()
    };

    try {
      const backendUrl = getBackendUrl();
      await axios.post(`${backendUrl}/api/settings`, {
        key_name: 'cookie_settings',
        value_data: cookieSettingsData
      }, { withCredentials: true });
      
      setSuccess("Çerez Politikası (KVKK) ayarları başarıyla kaydedildi!");
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error("Save settings failed", err);
      setError("Ayarlar kaydedilirken hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const copyId = (id) => {
    navigator.clipboard.writeText(id);
    setSuccess("Onay ID panoya kopyalandı!");
    setTimeout(() => setSuccess(''), 2000);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted_all':
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">Hepsini Kabul Etti</span>;
      case 'rejected_all':
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-rose-50 text-rose-700 border border-rose-200">Hepsini Reddetti</span>;
      case 'custom':
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-amber-50 text-amber-700 border border-amber-200">Özel Tercih</span>;
      default:
        return <span className="px-2.5 py-1 text-[10px] font-bold rounded-lg bg-slate-50 text-slate-700 border border-slate-200">{status}</span>;
    }
  };

  const parsePreferences = (log) => {
    try {
      const data = JSON.parse(log.consents_data);
      const output = [];
      if (data.necessary) output.push("Zorunlu");
      if (data.analytical) output.push("Analitik");
      if (data.marketing) output.push("Pazarlama");
      return output.join(', ');
    } catch (e) {
      return log.consents_data;
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-xs font-bold text-emerald-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-emerald-50/50">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
          <span>{success}</span>
        </div>
      )}
      {error && (
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl text-xs font-bold text-rose-700 flex items-center gap-2 animate-fade-in shadow-sm shadow-rose-50/50">
          <AlertCircle size={16} className="text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div>
        <h2 className="text-xl font-extrabold text-slate-800 tracking-tight flex items-center gap-2">
          <ShieldCheck className="text-blue-600" size={24} />
          Çerez Politikası ve KVKK İzin Takip Sistemi
        </h2>
        <p className="text-xs text-slate-500 mt-1">
          Kullanıcıların çerez politikası onaylarını kaydedin, kabul/ret istatistiklerini takip edin ve KVKK regülasyonlarına tam uyumluluk sağlayın.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200/60 p-4 rounded-2xl shadow-sm text-center">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Toplam Kayıt</p>
          <p className="text-xl font-extrabold text-slate-800 mt-1">{stats.total}</p>
        </div>
        <div className="bg-emerald-50/40 border border-emerald-100 p-4 rounded-2xl shadow-sm text-center">
          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Tümünü Kabul Edenler</p>
          <p className="text-xl font-extrabold text-emerald-700 mt-1">{stats.accepted}</p>
        </div>
        <div className="bg-rose-50/40 border border-rose-100 p-4 rounded-2xl shadow-sm text-center">
          <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider">Tümünü Reddedenler</p>
          <p className="text-xl font-extrabold text-rose-700 mt-1">{stats.rejected}</p>
        </div>
        <div className="bg-amber-50/40 border border-amber-100 p-4 rounded-2xl shadow-sm text-center">
          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Özel Seçim Yapanlar</p>
          <p className="text-xl font-extrabold text-amber-700 mt-1">{stats.custom}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Banner Settings Form (Left / 4 cols) */}
        <form onSubmit={handleSaveSettings} className="lg:col-span-4 bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Çerez Banner Ayarları</h3>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                checked={enabled} 
                onChange={(e) => setEnabled(e.target.checked)} 
                className="sr-only peer" 
              />
              <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          <div className="space-y-4">
            {/* Policy URL */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Çerez Politikası / Aydınlatma Metni Bağlantısı (URL)</label>
              <input
                type="text"
                required={enabled}
                value={policyUrl}
                onChange={(e) => setPolicyUrl(e.target.value)}
                placeholder="Örn: /sayfa/cerez-politikasi veya /kvkk"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            {/* Analytics Cookies Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Analitik Çerez Açıklaması</label>
              <textarea
                rows={3}
                value={analyticsDesc}
                onChange={(e) => setAnalyticsDesc(e.target.value)}
                placeholder="Açıklama metni..."
                className="w-full px-4 py-3 border border-slate-300 rounded-xl text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Marketing Cookies Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700">Pazarlama Çerez Açıklaması</label>
              <textarea
                rows={3}
                value={marketingDesc}
                onChange={(e) => setMarketingDesc(e.target.value)}
                placeholder="Açıklama metni..."
                className="w-full px-4 py-3 border border-slate-300 rounded-xl text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none"
              />
            </div>
          </div>

          <div className="pt-2 text-right">
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-600/10 disabled:opacity-50"
            >
              <Save size={16} />
              {loading ? "Kaydediliyor..." : "Ayarları Kaydet"}
            </button>
          </div>
        </form>

        {/* Consent Logs (Right / 8 cols) */}
        <div className="lg:col-span-8 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Kullanıcı Çerez Onay Kayıtları</h3>
            
            {/* Search and Filters */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchVal}
                  onChange={(e) => setSearchVal(e.target.value)}
                  placeholder="IP, Onay ID veya Parmak İzi Ara..."
                  className="pl-9 pr-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>

              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-slate-300 rounded-xl text-xs font-semibold bg-white focus:outline-none"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="accepted_all">Kabul Edenler</option>
                <option value="rejected_all">Reddedenler</option>
                <option value="custom">Özel Seçim</option>
              </select>

              <button
                onClick={fetchLogs}
                className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-600 rounded-xl border border-slate-200 transition"
                title="Yenile"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>

          {/* Logs Table Card */}
          <div className="bg-white border border-slate-200/60 rounded-3xl overflow-hidden shadow-sm flex flex-col justify-between min-h-[400px]">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 font-bold">
                    <th className="p-4">Tarih</th>
                    <th className="p-4">IP Adresi</th>
                    <th className="p-4">Onay Durumu</th>
                    <th className="p-4">Tercihler</th>
                    <th className="p-4">Cihaz Parmak İzi</th>
                    <th className="p-4">Onay ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400 italic">
                        <RefreshCw size={18} className="animate-spin inline mr-2" /> Yükleniyor...
                      </td>
                    </tr>
                  ) : logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400 italic">
                        Onay kaydı bulunamadı.
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-4 whitespace-nowrap text-slate-400 font-bold flex items-center gap-1.5">
                          <Calendar size={13} />
                          {new Date(log.date_created).toLocaleString()}
                        </td>
                        <td className="p-4 font-bold text-slate-800">{log.ip_address}</td>
                        <td className="p-4">{getStatusBadge(log.consent_status)}</td>
                        <td className="p-4 text-[10px] text-slate-500 font-bold">{parsePreferences(log)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-1.5 max-w-[120px] truncate" title={log.fingerprint || 'Belirtilmedi'}>
                            <Laptop size={13} className="text-slate-400 shrink-0" />
                            <span className="truncate font-mono text-[10px] bg-slate-50 px-1 py-0.5 rounded border border-slate-100 text-slate-500">{log.fingerprint || 'Yok'}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1">
                            <span className="font-mono text-[10px] text-slate-400">{log.consent_id.substring(0, 8)}...</span>
                            <button
                              onClick={() => copyId(log.consent_id)}
                              className="p-1 hover:bg-slate-100 text-slate-400 hover:text-slate-700 rounded transition shrink-0"
                              title="Tüm Onay ID'yi Kopyala"
                            >
                              <Copy size={11} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 p-4 bg-white gap-3 rounded-b-3xl">
                <span className="text-[11px] text-slate-500 font-bold">
                  Toplam {totalItems} kayıttan {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalItems)} arası gösteriliyor
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    className="px-3.5 py-2 text-xs font-bold bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl disabled:opacity-50 text-slate-700 transition"
                  >
                    Önceki
                  </button>
                  {[...Array(totalPages)].map((_, idx) => {
                    const pNum = idx + 1;
                    if (totalPages > 6 && pNum !== 1 && pNum !== totalPages && Math.abs(currentPage - pNum) > 1) {
                      if (pNum === 2 || pNum === totalPages - 1) {
                        return <span key={pNum} className="text-slate-400 px-1 font-bold">...</span>;
                      }
                      return null;
                    }
                    return (
                      <button
                        key={pNum}
                        onClick={() => setCurrentPage(pNum)}
                        disabled={loading}
                        className={`px-3 py-2 text-xs font-bold rounded-xl border transition ${
                          currentPage === pNum
                            ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-600/10'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {pNum}
                      </button>
                    );
                  })}
                  <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    className="px-3.5 py-2 text-xs font-bold bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl disabled:opacity-50 text-slate-700 transition"
                  >
                    Sonraki
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCookies;
